// utils/auth/loginPromptManager.ts
import sessionManager from '../session/sessionManager';

type PromptSettings = {
    frequency: number;
    maxPrompts: number;
};

type PromptSettingsMap = Record<LoginPromptType, PromptSettings>;
type PromptCountsMap = Record<LoginPromptType, number>;
type LastShownTimesMap = Record<LoginPromptType, number>;

// Types of login prompts
export enum LoginPromptType {
    CREATE_ACCOUNT = 'create_account',
    CONVERT_ANONYMOUS = 'convert_anonymous',
    SIGN_IN = 'sign_in'
}

// Session storage keys
const SEEN_PROMPTS_KEY = 'seen_login_prompts';
const PROMPT_LAST_SHOWN = 'login_prompt_last_shown';

// Default settings for different prompt types
const DEFAULT_SETTINGS: Record<LoginPromptType, PromptSettings> = {
    [LoginPromptType.CREATE_ACCOUNT]: {
        frequency: 720,
        maxPrompts: 3
    },
    [LoginPromptType.CONVERT_ANONYMOUS]: {
        frequency: 60,
        maxPrompts: 5
    },
    [LoginPromptType.SIGN_IN]: {
        frequency: 1440,
        maxPrompts: 2
    }
};
interface SeenPrompts {
    [key: string]: number; // Allow any string key
}
function createPromptTypeMap<T>(defaultValue: T): Record<LoginPromptType, T> {
    return {
        [LoginPromptType.CREATE_ACCOUNT]: defaultValue,
        [LoginPromptType.CONVERT_ANONYMOUS]: defaultValue,
        [LoginPromptType.SIGN_IN]: defaultValue
    };
}

export const loginPromptManager = {

    /**
     * Check if a specific prompt should be shown based on frequency and max shows
     */
    shouldShowPrompt: (promptType: LoginPromptType): boolean => {
        // Initialize with empty object if not present
        const seenPrompts = sessionManager.retrieveData<SeenPrompts>(SEEN_PROMPTS_KEY) || {};
        const lastShown = sessionManager.retrieveData<Record<string, number>>(PROMPT_LAST_SHOWN) || {};
        const settings = DEFAULT_SETTINGS[promptType];

        // Get count for this prompt type (default to 0 if not present)
        const promptCount = seenPrompts[promptType] || 0;

        // Check if we've shown this prompt the maximum number of times
        if (promptCount >= settings.maxPrompts) {
            return false;
        }

        // Check if it's too soon to show again
        const now = Date.now();
        const lastShownTime = lastShown[promptType] || 0;
        const minutesSinceLastShown = (now - lastShownTime) / (1000 * 60);

        return minutesSinceLastShown >= settings.frequency;
    },

    /**
     * Record that a prompt has been shown
     */
    recordPromptShown: (promptType: LoginPromptType): void => {
        // Get current seen counts
        const seenPrompts = sessionManager.retrieveData<Record<LoginPromptType, number>>(SEEN_PROMPTS_KEY) || createPromptTypeMap(0);

        // Get last shown times
        const lastShown = sessionManager.retrieveData<Record<LoginPromptType, number>>(PROMPT_LAST_SHOWN) ||
            {} as Record<LoginPromptType, number>;

        // Update counts and times
        seenPrompts[promptType]++;
        lastShown[promptType] = Date.now();

        // Save back to storage
        sessionManager.storeData(SEEN_PROMPTS_KEY, seenPrompts);
        sessionManager.storeData(PROMPT_LAST_SHOWN, lastShown);
    },

    /**
     * Reset prompt tracking for a specific type
     */
    resetPromptTracking: (promptType: LoginPromptType): void => {
        const seenPrompts = sessionManager.retrieveData<Record<LoginPromptType, number>>(SEEN_PROMPTS_KEY) ||
            createPromptTypeMap(0);

        const lastShown = sessionManager.retrieveData<Record<LoginPromptType, number>>(PROMPT_LAST_SHOWN) ||
            {} as Record<LoginPromptType, number>;

        // Reset specific prompt type
        seenPrompts[promptType] = 0;
        delete lastShown[promptType];

        // Save back to storage
        sessionManager.storeData(SEEN_PROMPTS_KEY, seenPrompts);
        sessionManager.storeData(PROMPT_LAST_SHOWN, lastShown);
    },

    /**
     * Reset all prompt tracking
     */
    resetAllPromptTracking: (): void => {
        sessionManager.clearData(SEEN_PROMPTS_KEY);
        sessionManager.clearData(PROMPT_LAST_SHOWN);
    },

    /**
     * Get appropriate prompt type based on user status
     */
    getAppropriatePromptType: (isAnonymous: boolean, isAuthenticated: boolean): LoginPromptType => {
        if (isAuthenticated) {
            if (isAnonymous) {
                // User is authenticated but using an anonymous account
                return LoginPromptType.CONVERT_ANONYMOUS;
            } else {
                // User is fully authenticated with a permanent account
                return LoginPromptType.SIGN_IN; // Default to sign in if they click the login button
            }
        } else {
            // User is not authenticated at all
            return LoginPromptType.SIGN_IN;
        }
    },
};